'use strict';
/**
 * ProvenanceObjectEditor — pict-section-objecteditor specialized to show, per value, WHICH SCOPE set
 * it (a colored left border on the row + a small scope badge after the value). It does this WITHOUT
 * overriding any template hash (which would risk clobbering other ObjectEditor instances): it appends
 * {~D:Record.ProvenanceBadgeHTML~} to the value/summary macros and a {~D:Record.ProvenanceRowClass~}
 * to RowOpen — macros are read from THIS view's own options, so the change is fully isolated. The
 * per-node fields are stamped by the swapped-in provenance node renderers (ProvenanceNodeRenderers).
 *
 * Options of note (set per-instance by the host, like ProjectWorkspace embeds the base editor):
 *   ObjectDataAddress      — the object to edit (e.g. 'AppData.ConfigViewer.EditingConfig')
 *   ProvenanceDataAddress  — the provenance map (e.g. 'AppData.ConfigViewer.ProvenanceMap'); empty map => no badges
 *   Editable               — read-only vs editable
 */

const libBaseObjectEditor = require('pict-section-objecteditor');
const libProvRenderers = require('./ProvenanceNodeRenderers.js');

const _BaseConfig = libBaseObjectEditor.default_configuration;
const _BaseMacros = _BaseConfig.MacroTemplates.Node;
const _Badge = '{~D:Record.ProvenanceBadgeHTML~}';

// Override only the macros that render a value/summary (append the badge) and RowOpen (scope class).
// Everything else is inherited unchanged. Templates are NOT touched.
const _ProvenanceMacros = Object.assign({}, _BaseMacros,
	{
		RowOpen: '<div class="pict-oe-row {~D:Record.ProvenanceRowClass~}" style="padding-left:{~D:Record.PaddingLeft~}px" data-path="{~D:Record.EscapedPath~}">',
		ValueStringEditable: _BaseMacros.ValueStringEditable + _Badge,
		ValueStringReadOnly: _BaseMacros.ValueStringReadOnly + _Badge,
		ValueNumberEditable: _BaseMacros.ValueNumberEditable + _Badge,
		ValueNumberReadOnly: _BaseMacros.ValueNumberReadOnly + _Badge,
		ValueBooleanEditable: _BaseMacros.ValueBooleanEditable + _Badge,
		ValueBooleanReadOnly: _BaseMacros.ValueBooleanReadOnly + _Badge,
		ValueNull: _BaseMacros.ValueNull + _Badge,
		// Summary renders for arrays (atomic leaf -> badged) AND objects (container -> empty badge).
		Summary: _BaseMacros.Summary + _Badge,
	});

const _ProvenanceCSS = /*css*/`
.cfg-prov-badge { display:inline-block; margin-left:.5rem; padding:0 .42rem; border-radius:9px; font-size:.62rem; font-weight:600; line-height:1.55; vertical-align:middle; letter-spacing:.02em; }
.pict-oe-row.cfg-prov-global { border-left:3px solid var(--theme-color-border-default,#dcdcdc); }
.cfg-prov-badge.cfg-prov-global { background:var(--theme-color-background-tertiary,#ececec); color:var(--theme-color-text-muted,#777); }
.pict-oe-row.cfg-prov-customer { border-left:3px solid var(--theme-color-brand-primary,#2e7d74); }
.cfg-prov-badge.cfg-prov-customer { background:var(--theme-color-brand-primary,#2e7d74); color:#fff; }
.pict-oe-row.cfg-prov-contract { border-left:3px solid var(--theme-color-info,#3098c8); }
.cfg-prov-badge.cfg-prov-contract { background:var(--theme-color-info,#3098c8); color:#fff; }
.pict-oe-row.cfg-prov-project { border-left:3px solid var(--theme-color-success,#3aa76d); }
.cfg-prov-badge.cfg-prov-project { background:var(--theme-color-success,#3aa76d); color:#fff; }
.pict-oe-row.cfg-prov-user { border-left:3px solid var(--theme-color-warning,#e0a800); }
.cfg-prov-badge.cfg-prov-user { background:var(--theme-color-warning,#e0a800); color:#fff; }
`;

class ProvenanceObjectEditor extends libBaseObjectEditor
{
	/**
	 * Build the default renderers, then swap in the provenance-aware subclasses (registered as service
	 * types and instantiated exactly like the base does).
	 */
	onBeforeInitialize()
	{
		const tmpResult = super.onBeforeInitialize();
		libProvRenderers.Renderers.forEach((pEntry) =>
		{
			if (!this.fable.servicesMap.hasOwnProperty(pEntry.ServiceType))
			{
				this.fable.addServiceType(pEntry.ServiceType, pEntry.Class);
			}
			const tmpRenderer = this.fable.instantiateServiceProviderWithoutRegistration(pEntry.ServiceType);
			tmpRenderer._ObjectEditorView = this;
			this._NodeRenderers[pEntry.TypeKey] = tmpRenderer;
		});
		return tmpResult;
	}
}

module.exports = ProvenanceObjectEditor;
module.exports.default_configuration = Object.assign({}, _BaseConfig,
	{
		ViewIdentifier: 'ProvenanceObjectEditor',
		ProvenanceDataAddress: 'AppData.ConfigViewer.ProvenanceMap',
		MacroTemplates: { Node: _ProvenanceMacros },
		CSS: (_BaseConfig.CSS || '') + _ProvenanceCSS,
	});
